import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { hasAnyRole } from "../auth/authUi";
import { CMMS_FIELD_ROLES } from "../features/cmms/cmmsRoles";
import { useIsCmmsEnabled } from "../features/cmms/cmmsFeature";
import { fetchActiveTool } from "../fetchers/cmmsToolMonitoring";
import { useToolServiceStatuses } from "./useToolServiceStatuses";
import { useLocalStorage } from "./useLocalStorage";

/*
 * How far the tool currently mounted in a machine has run against its service schedule.
 *
 * Two reads chained on an asset node: what is in the machine, then how that tool is doing.
 * Neither endpoint is new — `active-tool` was built for the quick report dialog and the status
 * read for the tools grid. This hook only puts them end to end.
 *
 * Gating lives here, not in the JSX around the caller. `HideIfNoRole` cannot help: hooks run
 * in the parent that mounts the component, so an ungated chain would fire on every machine
 * page load for users outside Cmms_Field and 403 twice. The machine page is reachable by far
 * more roles than /tools, which is route-gated to [1,2].
 */
export function useActiveToolServiceStatus(assetNodeId) {
  const { roleIds } = useAuth();
  const [companyKey] = useLocalStorage("companyKey", 1);
  const cmmsEnabled = useIsCmmsEnabled();

  const isCmmsUser = hasAnyRole(roleIds, CMMS_FIELD_ROLES);
  const canRead = cmmsEnabled && isCmmsUser && Boolean(companyKey);

  const activeToolQuery = useQuery(
    // The key QuickReportDialog already uses, so the badge and the dialog cannot disagree
    // about what is mounted, and whichever renders first warms the other.
    ["cmmsActiveTool", companyKey, assetNodeId],
    () => fetchActiveTool(companyKey, assetNodeId),
    { enabled: canRead && Boolean(assetNodeId), staleTime: 60000 },
  );

  const tool = activeToolQuery.data ?? null;
  const toolId = tool?.toolId ?? null;

  /*
   * Held off until a tool id exists. Passing `undefined` would not read "no tool" — the status
   * endpoint treats a missing toolId as "every watched tool in the company", which is the
   * tools grid's question, not this one.
   */
  const { statusByToolId, isLoading: statusLoading } = useToolServiceStatuses(
    toolId ?? undefined,
    { enabled: Boolean(toolId) },
  );

  return {
    // { machineId, toolId, toolName }, any of which may be null. A toolName without a toolId
    // means the production order names a tool with no row in the register.
    tool,
    status: toolId ? statusByToolId.get(toolId) ?? null : null,
    /*
     * Only the active-tool read, deliberately — a failure there means we do not know what is
     * mounted, which a caller must not render as "nothing is mounted". The status read failing
     * is a weaker thing: the tool is known, only its progress is not, and the status cell
     * already answers a missing reading with its dash.
     */
    isError: activeToolQuery.isError,
    // isInitialLoading, not isLoading: a disabled query stays status:"loading" forever in
    // react-query v4, so isLoading would report "still loading" permanently for a customer
    // with CMMS switched off or a machine with no asset node. useToolServiceStatuses already
    // folds the same way.
    isLoading: activeToolQuery.isInitialLoading || statusLoading,
  };
}

export default useActiveToolServiceStatus;
